/**
 * The Ratings page as the URL holds it: its filters. Each is one value, and
 * absent means any. Stars are a number, so that the URL reads `?stars=5`:
 * the router writes a string that looks like a number in quotes.
 */
export const STARS_FILTERS = [5, 4, 3, 2, 1, "dismissed"] as const
export type StarsFilter = (typeof STARS_FILTERS)[number]

export type RatingsSearch = {
  /** A subject type, as apps send it: "room". */
  type?: string
  stars?: StarsFilter
  /** `<experiment id>:<variation key>`. */
  variation?: string
}

const SUBJECT_TYPE = /^[a-z0-9_.-]{1,50}$/

function isStarsFilter(value: unknown): value is StarsFilter {
  return (STARS_FILTERS as readonly unknown[]).includes(value)
}
const VARIATION = /^[a-z0-9]{8,64}:[a-z0-9][a-z0-9_.-]{0,49}$/i

/** Whatever the URL says, a page that can be drawn: it never throws. */
export function parseRatingsSearch(search: Record<string, unknown>): RatingsSearch {
  const parsed: RatingsSearch = {}
  if (typeof search.type === "string" && SUBJECT_TYPE.test(search.type)) parsed.type = search.type
  // A number in the URL arrives as one; an old link may have it quoted.
  const stars =
    typeof search.stars === "string" && /^[1-5]$/.test(search.stars) ? Number(search.stars) : search.stars
  if (isStarsFilter(stars)) parsed.stars = stars
  if (typeof search.variation === "string" && VARIATION.test(search.variation)) {
    parsed.variation = search.variation
  }
  return parsed
}

/** The variation filter, split into what the query takes. */
export function variationOf(value: string | undefined): { experimentId: string; variationKey: string } | null {
  if (value === undefined) return null
  const at = value.indexOf(":")
  return at < 0 ? null : { experimentId: value.slice(0, at), variationKey: value.slice(at + 1) }
}
